import {
  CareRelatedInformation,
  ContactDetails,
  EmptyState,
  PageHeader,
  SiteRelatedInformation
} from '@lmc/ui-kit'
import PdfExport from 'components/pdf/PdfExport'
import { useRouter } from 'hooks/useRouter'
import { NAMESPACE } from 'locale'
import { serviceUserProfileConstants as supConstants } from 'locale/serviceUserProfile'
import { isBrowser, isTablet } from 'react-device-detect'
import { useTranslation } from 'react-i18next'
import { GeneralInformation, ProfileViewSection } from './components'
import { Carer, Home, ResidentItem, ResidentStatus } from '@lmc/ts-common'
import { useSelector } from 'react-redux'
import PersonalInformation from '@lmc/ui-kit/src/systemIcons/PersonalInformation/PersonalInformation'
import {
  getProfileViewSectionItems,
  scrollToTop,
  getCareRelatedFields
} from './utils/utils'
import { useEffect, useMemo, useRef } from 'react'
import useResident from 'hooks/useResident'
import { Spinner } from 'components/generic'
import { ProfileContent, ProfileWrapper } from './styledComponents'
import useHomeType from 'hooks/useHomeType'
import MenuGrid from 'screens/Residents/components/MenuGrid/MenuGrid'
import {
  PERSONAL_INFO_FIELDS,
  SITE_RELATED_FIELDS
} from './utils/profileScreenFields'
import { DescriptionsItemType } from 'antd/es/descriptions'
import { getAddressString } from './utils/getAddressString'
import { Text } from '@lmc/ui-kit/src/general'
import styled from 'styled-components'
import useFeatureFlags from 'hooks/useFeatureFlags'
import {
  AdjustmentCategoryDescriptionEnum,
  ImpairmentDescriptionEnum,
} from '@lmc/validations'

type ResidentProfileScreenProps = {
  testId?: string
}

export const ResidentProfileScreen = ({
  testId
}: ResidentProfileScreenProps) => {
  const { t } = useTranslation(NAMESPACE.SERVICE_USER_PROFILE)
  const topRef = useRef<HTMLDivElement | null>(null)
  const {
    navigate,
    params: { id }
  } = useRouter()

  const { hasRota } = useHomeType()
  const flags = useFeatureFlags({
    flagNames: [
      'rostering_parked_features',
      'keyworkers',
      'dols_mods_alignment',
      'reasonable_adjustments',
      'mods_demographics'
    ]
  })?.data

  const dolsModsAlignment = flags.dols_mods_alignment?.enabled
  const reasonableAdjustmentsEnabled = flags?.reasonable_adjustments?.enabled

  const flagsRosteringParkedFeaturesEnabled =
    flags?.rostering_parked_features?.enabled

  const { isPro, isEmar, isFromCanada, isDoLSOverride } = useHomeType()

  const isModsDemographicsEnabled = flags?.mods_demographics?.enabled

  const { resident, isLoading } = useResident({ id })

  const DEATH_DETAILS_FIELDS = useMemo(
    () => [
      {
        key: 'deathDate',
        label: t(supConstants.DATE_OF_DEATH)
      }
    ],
    [t]
  )

  const PERSONAL_INFO_FIELDS_TO_DISPLAY = useMemo(() => {
    const baseFields = PERSONAL_INFO_FIELDS.filter((elem) => {
      if (elem.key === 'carerGender') return flagsRosteringParkedFeaturesEnabled
      if (elem.key === 'gender') return !isModsDemographicsEnabled
      if (elem.key === 'genderIdentity' || elem.key === 'sex')
        return isModsDemographicsEnabled

      return true
    })

    if (resident?.status === ResidentStatus.deceased) {
      const dateOfBirthIndex = baseFields.findIndex(
        (field) => field.key === 'dateOfBirth'
      )
      if (dateOfBirthIndex !== -1) {
        return [
          ...baseFields.slice(0, dateOfBirthIndex + 1),
          ...DEATH_DETAILS_FIELDS,
          ...baseFields.slice(dateOfBirthIndex + 1)
        ]
      }
    }

    return baseFields
  }, [
    PERSONAL_INFO_FIELDS,
    flagsRosteringParkedFeaturesEnabled,
    isModsDemographicsEnabled,
    resident?.status,
    DEATH_DETAILS_FIELDS
  ])

  const home = useSelector(
    (state: { global: { home: Home } }) => state.global.home
  )

  const flagsKeyworkersEnabled = flags?.keyworkers?.enabled

  const keyworkersEnabled =
    flagsKeyworkersEnabled && home?.features?.goals?.enabled

  const pdfTitle = `${resident?.name?.first} ${resident?.name?.last} - Hospital Passport`

  const PDF_EXPORT_URL = `/api/list/residents/${id}/pdf/hospital-passport`

  const carers = useSelector(
    (state: { data: { carers: Carer[] } }) => state.data.carers
  )

  const careRelatedFields = getCareRelatedFields(
    isPro,
    isEmar,
    isFromCanada,
    isDoLSOverride,
    hasRota || false,
    dolsModsAlignment
  )

  useEffect(() => {
    scrollToTop(topRef)
  }, [])

  if (isLoading) {
    return (
      <Spinner
        data-testid={testId ? `${testId}-loading` : 'resident-profile-loading'}
      />
    )
  }

  if (resident === undefined) {
    return (
      <EmptyState
        title={t(supConstants.NO_RESIDENT)}
        data-testid={
          testId ? `${testId}-no-resident` : 'resident-profile-no-resident'
        }
      />
    )
  }

  const residentName = `${resident.name.first} ${resident.name.last}`

  return (
    <ProfileWrapper
      ref={topRef}
      data-testid={
        testId ? `${testId}-profile-wrapper` : 'resident-profile-wrapper'
      }
    >
      <PageHeader
        sticky
        title={`${residentName}'s ${t(supConstants.GENERAL_INFORMATION)}`}
        // This is a reused code snippet for the back button that's inside a header that's already inside the codebase. Might be a good idea to refactor it at some point
        onBack={
          !isDesktop
            ? () => navigate(`/residents/${id}/profile`, { replace: true })
            : undefined
        }
        data-testid={
          testId ? `${testId}-page-header` : 'resident-profile-page-header'
        }
      >
        <PdfExport
          pdfTitle={pdfTitle}
          hideIcon={!isDesktop}
          text={t(supConstants.HOSPITAL_PASSPORT)}
          url={PDF_EXPORT_URL}
          data-testid={
            testId ? `${testId}-pdf-export` : 'resident-profile-pdf-export'
          }
        />
      </PageHeader>
      <ProfileContent
        data-testid={
          testId ? `${testId}-profile-content` : 'resident-profile-content'
        }
      >
        <GeneralInformation resident={resident} home={home} testId={testId} />
        <MenuGrid testId={testId}>
          <ProfileViewSection
            title={t(supConstants.PERSONAL_INFORMATION)}
            icon={<PersonalInformation />}
            items={getProfileViewSectionItems(
              PERSONAL_INFO_FIELDS_TO_DISPLAY,
              resident,
              carers,
              isFromCanada,
              keyworkersEnabled
            )}
            action={() =>
              navigate(`../personal-information`, { relative: 'path' })
            }
            isDesktop={isDesktop}
            testId={testId}
          />
          <ProfileViewSection
            title={t(supConstants.SITE_RELATED_INFORMATION)}
            icon={<SiteRelatedInformation />}
            items={getProfileViewSectionItems(
              SITE_RELATED_FIELDS,
              resident,
              carers,
              isFromCanada
            )}
            action={() =>
              navigate(`../site-related-information`, { relative: 'path' })
            }
            isDesktop={isDesktop}
            testId={testId}
          />
          {isPro && (
            <ProfileViewSection
              title={t(supConstants.CARE_RELATED_INFORMATION)}
              icon={<CareRelatedInformation />}
              items={[
                ...getProfileViewSectionItems(
                  careRelatedFields || [],
                  resident,
                  carers,
                  isFromCanada,
                  undefined,
                  t(supConstants.NHS_NUMBER_STATUS_INFO)
                ),
                ...getCareInformationViewSectionItems(resident, t),
                ...(reasonableAdjustmentsEnabled ? getReasonableAdjustmentsViewSectionItems(resident, t) : []),
                ...(resident.otherIdentifiers?.map((identifier, index) => ({
                  key: `other-identifier-${index}-${identifier?.identifier}-${identifier?.type}`,
                  label: <Text>{identifier?.type}</Text>,
                  children: <Text>{identifier?.identifier}</Text>
                })) || [])
              ]}
              action={() =>
                navigate(`../care-related-information`, { relative: 'path' })
              }
              isDesktop={isDesktop}
              testId={testId}
            />
          )}
          {isPro && (
            <ProfileViewSection
              title={t(supConstants.CONTACT_DETAILS)}
              icon={<ContactDetails />}
              items={getContactDetailsViewSectionItems(resident, t)}
              action={() =>
                navigate(`../contact-details`, { relative: 'path' })
              }
              isDesktop={isDesktop}
              testId={testId}
            />
          )}
        </MenuGrid>
      </ProfileContent>
    </ProfileWrapper>
  )
}

const isDesktop = isBrowser || isTablet

const getContactDetailsViewSectionItems = (
  resident: ResidentItem,
  t: (key: string) => string
): DescriptionsItemType[] => {
  const addressesItems: DescriptionsItemType[] = resident.addresses.map(
    (address, index) => ({
      key: `address-${index}-${address.street1}`,
      label: <Text>{t(supConstants[address.type])}</Text>,
      children: <Text>{getAddressString(address)}</Text>
    })
  )
  const phonesItems: DescriptionsItemType[] = resident.phones.map(
    (phone, index) => ({
      key: `phone-${index}-${phone.number}`,
      label: <Text>{t(supConstants[`${phone.type}_NUMBER`])}</Text>,
      children: <Text>{phone.number}</Text>
    })
  )

  const items: DescriptionsItemType[] = [
    ...addressesItems,
    ...phonesItems,
    {
      key: 'preferredContactMethod',
      label: <Text>{t(supConstants.PREFERRED_CONTACT_METHOD)}</Text>,
      children: resident.preferredContactMethod && (
        <Text>{t(supConstants[resident.preferredContactMethod])}</Text>
      )
    },
    {
      key: 'email',
      label: <Text>{t(supConstants.EMAIL)}</Text>,
      children: resident.email && <Text>{resident.email}</Text>
    }
  ]

  const filteredValues = items.filter((item) => item.children)

  return filteredValues
}

const getCareInformationViewSectionItems = (
  resident: ResidentItem,
  t: (key: string) => string
): DescriptionsItemType[] => {
  if (resident.residentAllergies && resident.residentAllergies.length === 0) {
    return []
  }

  const hasNoKnownAllergies = resident.noKnownAllergies === true

  const allergiesItems: DescriptionsItemType[] = resident.residentAllergies.map(
    (allergy, index) => ({
      key: `allergy-${index}-${allergy.causativeAgent}`,
      label: <Text>{t(supConstants.ALLERGY_TYPE)}</Text>,
      children: (
        <AllergyViewSection>
          {!hasNoKnownAllergies && (
            <>
              <Text>{allergy.causativeAgent}</Text>
              <SubText level={2}>{allergy.typeOfReaction}</SubText>
              <SubText level={2}>{allergy.severityCode}</SubText>
              <SubText level={2}>{allergy.severityDescription}</SubText>
            </>
          )}
          {hasNoKnownAllergies && (
            <Text>{t(supConstants.NO_KNOWN_ALLERGIES)}</Text>
          )}
        </AllergyViewSection>
      )
    })
  )

  const filteredValues = allergiesItems.filter((item) => item.children)

  return filteredValues
}

const AllergyViewSection = styled.div`
  display: flex;
  flex-direction: column;
`

const SubText = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color5};
`

const mapCodesToLabels = (
  codes: string[] | undefined,
  enumMap: Record<string, string>
): string | null => {
  if (!codes || codes.length === 0) return null
  // Fallback to raw code if label not found (should not normally happen)
  return codes.map((code) => enumMap[code] || code).join(', ')
}

const getReasonableAdjustmentsViewSectionItems = (
  resident: ResidentItem,
  t: (key: string) => string
): DescriptionsItemType[] => {
  if (!resident.reasonableAdjustments?.length) return []

  return resident.reasonableAdjustments.map((adjustment, index) => {
    const categoryLabels = mapCodesToLabels(adjustment.adjustmentCategory, AdjustmentCategoryDescriptionEnum)
    const impairmentLabels = mapCodesToLabels(adjustment.impairment, ImpairmentDescriptionEnum)

    return {
      key: `reasonable-adjustment-${index}`,
      label: <Text>{t(supConstants.REASONABLE_ADJUSTMENTS)}</Text>,
      children: (
        <AllergyViewSection>
          {impairmentLabels && <Text>{impairmentLabels}</Text>}
          {categoryLabels && <Text>{categoryLabels}</Text>}
        </AllergyViewSection>
      )
    }
  })
}
